import { getConfig } from '@/configs/config-accessor';
import ApiService from './ApiService';

// Get the API prefix from the config accessor
const getApiPrefix = () => getConfig().newApiPrefix;

export interface IAuth {
  user: string;
  ID: string;
  _id: string;
  name: string;
  Name: string;
  userName: string;
  UserName: string;
  email: string;
  phoneNumber: string;
  business: string;
  status: 'pending' | 'active' | 'blocked';
}

export interface IUserAuthToken {
  device?: string;
  browser?: string;
  os?: string;
}

export type Signup_Body = Pick<IAuth, 'name' | 'userName' | 'email' | 'phoneNumber'> & { password: string };
export type Signup_Response = IResponse<null>;
export const SignupUser = async (body: Signup_Body) => {
  const newApiPrefix = getApiPrefix();
  return ApiService.fetchData<Signup_Response, Signup_Body>({
    url: `${newApiPrefix}/auth/signup`,
    method: 'post',
    data: body,
  });
};

export type Invite_Body = Pick<IAuth, 'name' | 'userName' | 'email' | 'phoneNumber'> & { loginCode: string; role: { label: string; value: string } };
export type Invite_Response = IResponse<null>;
export const InviteUser = async (body: Invite_Body) => {
  const newApiPrefix = getApiPrefix();
  return ApiService.fetchData<Invite_Response, Invite_Body>({
    url: `${newApiPrefix}/auth/invite`,
    method: 'post',
    data: body,
  });
};

export type ResendConfirmationEmail_Body = Pick<IAuth, 'userName'>;
export type ResendConfirmationEmail_Response = IResponse<null>;
export const ResendUserConfirmationEmail = async (body: ResendConfirmationEmail_Body) => {
  const newApiPrefix = getApiPrefix();
  return ApiService.fetchData<ResendConfirmationEmail_Response, ResendConfirmationEmail_Body>({
    url: `${newApiPrefix}/auth/resend-confirmation`,
    method: 'post',
    data: body,
  });
};

export type Signin_Body = Pick<IAuth, 'userName'> & { password: string; companyName?: string };
export type Signin_Response = IResponse<(IAuth & { token: string }) | null>;
export const SigninUser = async (body: Signin_Body) => {
  let apiPrefix = getApiPrefix();

  if (body.companyName) {
    const baseUrl = getConfig().newApiPrefix.replace(`/${body.companyName}`, ''); 
    apiPrefix = `${baseUrl}/${body.companyName}`;
  }

  return ApiService.fetchData<Signin_Response, Signin_Body>({
    url: `${apiPrefix}/auth/loginbyusernamenpassword`,
    method: 'post',
    data: body,
  });
};

export type Signout_Params = { session: 'current' } | { session: 'all' } | { session: 'device'; deviceId: string };
export type Signout_Response = IResponse<null>;
export const SignoutUser = async (params: Signout_Params) => {
  const newApiPrefix = getApiPrefix();
  return ApiService.fetchData<Signin_Response, Signin_Body>({
    url: `${newApiPrefix}/auth/signout`,
    method: 'post',
    params: params,
  });
};

export type GetAllSessions_Response = IResponse<IUserAuthToken[] | null>;
export const GetAllSessionsOfUser = async () => {
  const newApiPrefix = getApiPrefix();
  return ApiService.fetchData<GetAllSessions_Response, undefined>({
    url: `${newApiPrefix}/auth/get-all-sessions`,
    method: 'get',
  });
};

export type ForgotPassword_Body = Pick<IAuth, 'email'>;
export type ForgotPassword_Response = IResponse<null>;
export const ForgotPassword = async (body: ForgotPassword_Body) => {
  const newApiPrefix = getApiPrefix();
  return ApiService.fetchData<ForgotPassword_Response, ForgotPassword_Body>({
    url: `${newApiPrefix}/auth/forgot-password`,
    method: 'post',
    data: body,
  });
};

export type ResetPassword_Body = { password: string };
export type ResetPassword_Params = { token: string };
export type ResetPassword_Response = IResponse<null>;
export const ResetPassword = async (params: ResetPassword_Params, body: ResetPassword_Body) => {
  const newApiPrefix = getApiPrefix();
  return ApiService.fetchData<ResetPassword_Response, ResetPassword_Body>({
    url: `${newApiPrefix}/auth/reset-password`,
    method: 'post',
    data: body,
    params: params,
  });
};
